/**
 * F20 (Wave 2D) — admin resolve-dispute modal.
 *
 * Two-outcome SegmentedControl (`customer_correct` | `venue_correct`) +
 * optional resolution-notes Textarea (max 1000) + per-outcome preview Banner
 * that explains the downstream impact:
 *   - customer_correct → "Payment will revert to UNPAID; F23 will reverse commission"
 *   - venue_correct    → "Payment stays PAID; no commission change"
 *
 * Admin CANNOT pick `withdrawn` from this dialog (Decision 7 — customer-only
 * withdraw via the consumer-side API). SegmentedControl has 2 options.
 *
 * Gated by the parent page on `payment_disputes:resolve`. Finance role sees a
 * disabled/hidden button (not this dialog).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, SegmentedControl, Textarea } from '@bravemove/ui-web';
import {
  DISPUTE_RESOLUTION_OUTCOMES,
  ResolveDisputeInputSchema,
  type DisputeResolutionOutcome,
  type PaymentDispute,
  type ResolveDisputeInput,
} from '@bravemove/types';

export interface ResolveDisputeDialogProps {
  readonly dispute: PaymentDispute;
  readonly open: boolean;
  readonly onConfirm: (input: ResolveDisputeInput) => Promise<void>;
  readonly onClose: () => void;
}

export function ResolveDisputeDialog({
  dispute,
  open,
  onConfirm,
  onClose,
}: ResolveDisputeDialogProps) {
  const { t } = useI18n();
  const [outcome, setOutcome] = useState<DisputeResolutionOutcome>('customer_correct');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset every open so a previous abort doesn't leak into the next attempt.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setOutcome('customer_correct');
    setNotes('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: ResolveDisputeInput = useMemo(
    () => (notes.trim() ? { outcome, resolutionNotes: notes } : { outcome }),
    [outcome, notes],
  );

  const validation = useMemo(() => ResolveDisputeInputSchema.safeParse(payload), [payload]);

  const confirmDisabled = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const previewBanner = (() => {
    if (outcome === 'customer_correct') {
      return (
        <div data-testid="resolve-dispute-preview-customer-correct">
          <Banner
            tone="warning"
            title={t('payment:dialog.resolve_dispute.preview.customer_correct.title', {
              defaultValue: 'Payment will revert to UNPAID',
            })}
          >
            {t('payment:dialog.resolve_dispute.preview.customer_correct.body', {
              defaultValue:
                'The booking moves back to UNPAID via an admin override; any accrued commission will be reversed automatically.',
            })}
          </Banner>
        </div>
      );
    }
    return (
      <div data-testid="resolve-dispute-preview-venue-correct">
        <Banner
          tone="info"
          title={t('payment:dialog.resolve_dispute.preview.venue_correct.title', {
            defaultValue: 'Payment stays PAID',
          })}
        >
          {t('payment:dialog.resolve_dispute.preview.venue_correct.body', {
            defaultValue:
              'The booking remains PAID. No commission change. The customer will be notified that the venue was correct.',
          })}
        </Banner>
      </div>
    );
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('payment:dialog.resolve_dispute.title', { defaultValue: 'Resolve dispute' })}
      description={t('payment:dialog.resolve_dispute.body', {
        defaultValue: 'Choose who is correct and optionally add resolution notes.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="resolve-dispute-cancel"
          >
            {t('payment:action.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={confirmDisabled}
            data-testid="resolve-dispute-confirm"
          >
            {t('payment:dialog.resolve_dispute.submit', { defaultValue: 'Resolve' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="resolve-dispute-dialog">
        <FormField
          label={t('payment:dialog.resolve_dispute.outcome_label', { defaultValue: 'Outcome' })}
          required
        >
          <SegmentedControl<DisputeResolutionOutcome>
            value={outcome}
            onChange={(v) => setOutcome(v)}
            options={DISPUTE_RESOLUTION_OUTCOMES.map((o) => ({
              value: o,
              label: t(`payment:outcome.${o}`, {
                defaultValue: o === 'customer_correct' ? 'Customer correct' : 'Venue correct',
              }),
            }))}
            aria-label={t('payment:dialog.resolve_dispute.outcome_label', {
              defaultValue: 'Outcome',
            })}
          />
        </FormField>

        {previewBanner}

        <FormField
          label={t('payment:dialog.resolve_dispute.notes_label', {
            defaultValue: 'Resolution notes (optional, admin-only)',
          })}
        >
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('payment:dialog.resolve_dispute.notes_placeholder', {
              defaultValue: 'Optional context for the audit log (max 1000 chars).',
            })}
            rows={3}
            maxLength={1000}
            data-testid="resolve-dispute-notes"
          />
        </FormField>

        <p className="text-[11px] text-ink-3 font-mono tabular-nums">
          {t('payment:dialog.resolve_dispute.dispute_ref', {
            defaultValue: 'Dispute id',
          })}
          : {dispute.id}
        </p>
      </div>
    </Modal>
  );
}
